import i18next from "i18next";
import { CREDITS, WEBP_SUPPORT } from "../Settings";

/**
 * Parse credits to be used below media objects and in lightbox modals
 * @param {object} credits Credit list from the media API
 * @param {string} language Language for the credit labels (currentLanguage). Pass it explicitly: components update
 *   before App switches the i18n language, so relying on the active i18n language gives the previous language.
 * @returns {string} Stringifies list
 */
 export const parseCredits = (credits: any, language?: string) => {
  const creditTypes = CREDITS;

  let creditList: any = [];
  credits?.forEach((credit, i) => {
    if (credit?.name) {
      creditList.push(`${i18next.t(creditTypes[credit?.credit_type_id], language ? { lng: language } : undefined)}: ${credit?.name}`);
    }
  });
  return creditList?.join(", ");
}

/**
 * Fade in image when loaded
 * @param e ChangeEvent
 */
 export const showImageWhenLoaded = (e: React.ChangeEvent<HTMLImageElement>) => {
  e.target.classList.remove("loading");
  e.target.style.opacity = "1";
  e.target.style.visibility = "visible";
}

/** Image URLs that have loaded (or been preloaded) */
const loadedImages = new Set<string>();

/** Whether an image URL has loaded, so it can be shown at once instead of fading in */
export const isImageLoaded = (url?: string): boolean => Boolean(url && loadedImages.has(url));

/** Remember that an image URL has loaded */
export const markImageLoaded = (url?: string) => { url && loadedImages.add(url); };

/**
 * Load images before they are shown, e.g. the next page of cards, so they appear in one go instead of
 * flashing empty. Resolves when all have loaded or failed, or after a time limit
 * @param {string[]} urls Image URLs
 * @param {number} timeout Max wait in ms
 */
export const preloadImages = (urls: string[], timeout: number = 2000): Promise<void> => {
  const loads = urls.filter(url => url && !loadedImages.has(url)).map(url => new Promise<void>(resolve => {
    const image = new Image();
    image.onload = () => { loadedImages.add(url); resolve(); };
    image.onerror = () => resolve();
    image.src = url;
  }));
  return Promise.race([Promise.all(loads).then(() => undefined), new Promise<void>(resolve => setTimeout(resolve, timeout))]);
}

/**
 * Generate CSS filters from object
 * @param filters Filters. Example: { contrast: 1, brightness: 0.5 }
 * @returns {string} Filter CSS. Example: "contrast(1) brightness(0.5)"
 */
 export const generateCSSFilters = (filters: any) => {
  if (!filters) { return undefined; }
  
  let cssFilter: string[] = [];

  for (const prop in filters) {
    if (filters?.[prop]) {
      cssFilter?.push(`${prop}(${filters?.[prop]})`);
    }
  }

  return cssFilter?.join(" ");
}

/**
 * Get image URL with media type parameter
 * @param url {string} URL
 * @param options {string} Options
 * @returns Image URL with media type
 */
export const getImageUrl = (url: string, options?: { transparency?: boolean; dimension?: string }): string => {
  // Force use of EMS
  url = verifyEmsUrl(url);

  // Trygg parsing av URL
  const parsedUrl = new URL(url, typeof window !== "undefined" ? window.location.origin : "https://dummy.base");
  const params = parsedUrl.searchParams;

  // Sett mediaType
  if (!WEBP_SUPPORT) {
    const mediaType = options?.transparency ? "image/png" : "image/jpg";
    params.set("mediaType", mediaType);
  }

  // Sett eller overskriv dimension
  if (options?.dimension) {
    params.set("dimension", options.dimension);
  }

  // Rekonstruer URL-en
  parsedUrl.search = params.toString();
  return parsedUrl.toString();
};


/**
 * Force use of EMS
 * @param url Url to check
 * @returns {string} Verified URL
 */
export const verifyEmsUrl = (url: string) => {
  url = url.replace(/http:\/\/(?!localhost)/g, "https://");
  url = url.replace("https://mm.dimu.org", "https://ems.dimu.org");
  url = url.replace("https://dms02.dimu.org", "https://ems.dimu.org");
  // Older media can point to a CDN node (e.g. ems-cf-01), which does not load
  url = url.replace(/https:\/\/ems-cf-\d+\.dimu\.org/, "https://ems.dimu.org");
  url = url.replace("https://dms-test.dimu.org", "https://ems-test.dimu.org");

  return url;
}

/**
 * Verify if the webp image format is supported
 * @returns {boolean} Returns false if not supported
 */
export const verifyWebpSupport = (): Promise<boolean> => {
  return new Promise((resolve) => {
    const img = new Image();
    img.src = 'data:image/webp;base64,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';
    img.onload = () => resolve(img.width > 0 && img.height > 0);
    img.onerror = () => resolve(false);
  });
};

/**
 * Get dimension of image
 * @param {string} src Image url
 * @returns {object} Width and height of image
 */
export const getImageDimensions = (src: string) => {
  return new Promise((resolve, reject) => {
    //resolve({ width: 600, height: 300 })
    const img = new Image()
    img.onload = () => resolve({width: img.width * 3, height: img.height * 3})
    img.onerror = reject
    img.src = src + `?dimension=800x800`
  })
}

/**
 * Convert hex color code to rgb
 * @param {string} hex Hex color code
 * @param {number} opacity Opacity to append. Optional
 * @returns {string} Rgb color code
 */
export const hex2rgb = (hex: string, opacity?: number) => {
  if (typeof hex !== "string") { return }

  const r = parseInt(hex?.slice(1, 3), 16);
  const g = parseInt(hex?.slice(3, 5), 16);
  const b = parseInt(hex?.slice(5, 7), 16);
  
  return `rgb(${r} ${g} ${b} / ${opacity || 0}%)`;
}